import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Collapse, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import PhoneIcon from '@untitled-ui/icons-react/build/esm/Phone';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import type { Page as PageType } from 'src/types/page';

// ---------------------------------------------------------------------------
// Shared design tokens / helpers, mirrored from src/sections/services/
// service-page.tsx and src/pages/index.tsx so this custom page matches the
// rest of the site exactly (Inter + Roboto Mono, #0a0a0b / #2563eb / #06b6d4
// brand palette, scroll-reveal, section shells, collapsible FAQ).
// ---------------------------------------------------------------------------

const BRAND_GRADIENT = 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)';

const gradientTextSx = {
  background: BRAND_GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
} as const;

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: BRAND_GRADIENT }} />
    <Typography
      sx={{
        fontFamily: '"Roboto Mono", monospace',
        fontSize: 11,
        fontWeight: 500,
        color: '#0a0a0b',
        letterSpacing: 1.4,
        textTransform: 'uppercase'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const sectionShellSx = {
  py: { xs: 12, md: 18 },
  borderTop: '1px solid #f4f4f5'
} as const;

const sectionTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: '36px', md: '60px' },
  fontWeight: 700,
  lineHeight: 1.04,
  letterSpacing: '-0.035em',
  color: '#0a0a0b'
} as const;

// Collapsible FAQ item, matched to the home page accordion (src/pages/index.tsx).
const FaqItem = ({ q, a, i, isOpen, onToggle }: { q: string; a: string; i: number; isOpen: boolean; onToggle: () => void }) => (
  <Box
    onClick={onToggle}
    sx={{
      cursor: 'pointer',
      position: 'relative',
      px: { xs: 3, md: 4 },
      py: { xs: 3, md: 3.5 },
      borderRadius: 3,
      background: isOpen ? 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)' : '#ffffff',
      border: '1px solid',
      borderColor: isOpen ? '#2563eb' : '#e4e4e7',
      boxShadow: isOpen
        ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
        : '0 1px 2px rgba(15, 23, 42, 0.04)',
      transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
      '&:hover': {
        borderColor: isOpen ? '#2563eb' : '#a1a1aa',
        transform: isOpen ? 'none' : 'translateY(-1px)',
        boxShadow: isOpen
          ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
          : '0 4px 12px rgba(15, 23, 42, 0.06)'
      }
    }}
  >
    <Stack direction="row" alignItems="center" spacing={{ xs: 2, md: 3 }}>
      <Typography
        sx={{
          fontFamily: '"Roboto Mono", monospace',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.8,
          color: isOpen ? '#2563eb' : '#a1a1aa',
          minWidth: 32,
          transition: 'color 0.2s ease'
        }}
      >
        {String(i + 1).padStart(2, '0')}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: { xs: 16, md: 18 },
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: '#0a0a0b',
          flex: 1,
          textAlign: 'left'
        }}
      >
        {q}
      </Typography>
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: isOpen ? BRAND_GRADIENT : '#fafafa',
          color: isOpen ? '#ffffff' : '#0a0a0b',
          border: isOpen ? 'none' : '1px solid #e4e4e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          fontSize: 20,
          fontWeight: 300,
          lineHeight: 1,
          boxShadow: isOpen ? '0 6px 16px rgba(37, 99, 235, 0.3)' : 'none'
        }}
      >
        +
      </Box>
    </Stack>
    <Collapse in={isOpen} timeout={300}>
      <Box sx={{ mt: 2.5, pt: 2.5, pl: { xs: 0, md: 7 }, borderTop: '1px solid #f4f4f5' }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: 14, md: 15.5 },
            fontWeight: 400,
            lineHeight: 1.65,
            color: '#52525b',
            textAlign: 'left'
          }}
        >
          {a}
        </Typography>
      </Box>
    </Collapse>
  </Box>
);

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

const SEO_TITLE = 'Google Business Profile Optimization Houston | Mystic Media';
const SEO_DESCRIPTION =
  'Mystic Media Film optimizes your Google Business Profile (formerly Google My Business/GMB) so Houston & Katy customers find you in the Google Map Pack.';
const PAGE_URL = 'https://mysticmediafilm.com/services/google-business-profile-optimization';

const outcomes = ['Map Pack', 'Calls', 'Directions', 'Reviews', 'Website clicks'];

// Image showcase, what we tune on your profile (Google product screenshots).
const optimizeItems: { img: string; alt: string; label: string; body: string }[] = [
  {
    img: '/assets/gbp/gbp-feature-1.webp',
    alt: 'Google Business Profile showing accurate business name, address, hours, phone, and website',
    label: 'Business info',
    body: 'Your name, address, phone, hours, and service areas, accurate and consistent everywhere so Google and customers trust your listing.'
  },
  {
    img: '/assets/gbp/gbp-feature-2.webp',
    alt: 'Photos tab of a Google Business Profile filled with high-quality business photos',
    label: 'Photos & video',
    body: 'A full, on-brand photo set that makes you look the part and earns far more clicks than an empty profile.'
  },
  {
    img: '/assets/gbp/gbp-img-5.webp',
    alt: 'Google Business Profile review summary with a 4.5 star rating and customer reviews',
    label: 'Reviews',
    body: 'A simple system to earn more 5-star reviews and reply to every one. Reviews are a top-three local ranking factor.'
  },
  {
    img: '/assets/gbp/gbp-cards-2.webp',
    alt: 'Products and services listed directly on a Google Business Profile',
    label: 'Products & services',
    body: 'Your full catalog, listed and searchable right on your profile. It is one of the biggest levers on what you rank for.'
  },
  {
    img: '/assets/gbp/gbp-img-4.webp',
    alt: 'A Google Post with a new product update published to a Business Profile',
    label: 'Posts & offers',
    body: 'Regular updates, offers, and events posted to your profile so it stays active and turns searchers into buyers.'
  },
  {
    img: '/assets/gbp/gbp-img-7.webp',
    alt: 'Questions and answers section on a Google Business Profile',
    label: 'Questions & answers',
    body: 'We seed, monitor, and answer the questions buyers ask, before a competitor or a wrong answer gets there first.'
  }
];

// On-brand "insights" panel data (custom graphic, built in the brand palette).
const insightStats = [
  { label: 'Direction requests', value: '1,204', pct: 90 },
  { label: 'Website clicks', value: '890', pct: 66 },
  { label: 'Calls', value: '312', pct: 46 },
  { label: 'Messages', value: '146', pct: 26 }
];

// On-brand "lead actions" pills (custom graphic).
const leadActions = ['Call', 'Message', 'Get directions', 'Book online'];

const deliverables = [
  { num: '01', title: 'Profile audit', body: 'A full audit of your profile against current Google ranking signals and your top Houston competitors, with every gap mapped out.' },
  { num: '02', title: 'Accurate info', body: 'Name, address, phone, hours, categories, and service areas dialed in across the board so Google trusts your listing.' },
  { num: '03', title: 'Categories & services', body: 'The right primary and secondary categories, plus a complete services list. It is the strongest lever on what you show up for.' },
  { num: '04', title: 'Photos & media', body: 'Optimized, properly named photos and video that lift clicks and help your profile stand out in the Map Pack.' },
  { num: '05', title: 'Reviews engine', body: 'A done-for-you flow to request more 5-star reviews and respond to all of them, fast.' },
  { num: '06', title: 'Map Pack ranking', body: 'On-profile and local-signal optimization aimed squarely at the Google Map Pack, the top three local results.' }
];

const process = [
  { num: '01', title: 'Audit', body: 'We claim or verify your profile and audit it against current Google ranking factors and your local competitors.' },
  { num: '02', title: 'Optimize', body: 'Categories, services, info, photos, and posts. Every field filled in and tuned for the searches that bring you customers.' },
  { num: '03', title: 'Manage', body: 'Ongoing posts, review responses, and Q&A keep your profile active so it climbs and holds its spot.' },
  { num: '04', title: 'Report', body: 'Clear monthly reporting on calls, clicks, direction requests, and Map Pack movement, all in plain English.' }
];

const whyUs = [
  { title: 'Local to Houston', body: 'We optimize for the Houston and Katy map, not a generic checklist. We know the searches that actually put customers in front of you.' },
  { title: 'Done-for-you', body: 'You run your business and we run your profile. Setup, optimization, posts, photos, and reviews, all handled.' },
  { title: 'Real reporting', body: 'You see the same numbers Google sees: calls, direction requests, and website clicks, every month, with no fluff.' }
];

const faqs = [
  {
    q: 'Is Google Business Profile the same as Google My Business?',
    a: 'Yes. “Google Business Profile” is just Google’s current name for what used to be called Google My Business, or GMB. It’s the same free listing on Google Search and Maps. Google renamed it back in 2021, and we optimize it under either name.'
  },
  {
    q: 'Is a Google Business Profile free?',
    a: 'Yes. Creating and listing your business on Google is completely free. What we provide is the expert optimization and ongoing management that actually gets the listing to rank and bring in calls, direction requests, and customers.'
  },
  {
    q: 'How long until I rank in the Google Map Pack?',
    a: 'Most clients see movement within 30 to 90 days. Competitive Houston categories take longer, so we set a realistic timeline up front and report progress every month so you always know where you stand.'
  },
  {
    q: 'Do I need a physical storefront?',
    a: 'No. Service-area businesses that travel to the customer can rank too. We set up your service areas correctly so you show up across the Houston metro without publishing a street address.'
  },
  {
    q: 'What do you need from me to get started?',
    a: 'Access to your Google Business Profile, or help creating and verifying it, your correct business details, and a handful of photos. We handle everything else.'
  },
  {
    q: 'Do you only work with Houston businesses?',
    a: 'Houston and Katy are home, but we optimize Google Business Profiles for businesses across Texas and the U.S. Local ranking works the same way everywhere.'
  }
];

// ---------------------------------------------------------------------------
// Structured data
// ---------------------------------------------------------------------------

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${PAGE_URL}#service`,
  name: 'Google Business Profile Optimization',
  alternateName: 'Google My Business (GMB) Optimization',
  description: SEO_DESCRIPTION,
  url: PAGE_URL,
  serviceType: 'Google Business Profile Optimization',
  provider: { '@id': 'https://mysticmediafilm.com/#organization' },
  areaServed: [
    { '@type': 'City', name: 'Houston' },
    { '@type': 'City', name: 'Katy' },
    { '@type': 'AdministrativeArea', name: 'Greater Houston Metropolitan Area' },
    { '@type': 'AdministrativeArea', name: 'Harris County' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County' }
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Google Business Profile Optimization deliverables',
    itemListElement: deliverables.map((d) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: d.title, description: d.body }
    }))
  }
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${PAGE_URL}#faq`,
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a }
  }))
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://mysticmediafilm.com/' },
    { '@type': 'ListItem', position: 2, name: 'Google Business Profile Optimization', item: PAGE_URL }
  ]
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Page: PageType = () => {
  usePageView();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <Seo title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalUrl={PAGE_URL} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main style={{ background: '#ffffff' }}>
        {/* ---------------------------------------------------------------- */}
        {/* Hero (text only, matching the other service pages) */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={{ background: '#ffffff', position: 'relative', pt: { xs: '140px', md: '180px' }, pb: { xs: '60px', md: '90px' } }}>
          <Container maxWidth="lg">
            <Stack spacing={{ xs: 4, md: 5 }} sx={{ maxWidth: 980, mx: 'auto', textAlign: 'center', alignItems: 'center' }}>
              <Stack direction="row" justifyContent="center">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    pl: 0.75,
                    pr: 2,
                    py: 0.5,
                    borderRadius: 999,
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: BRAND_GRADIENT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: 12,
                      fontWeight: 700
                    }}
                  >
                    ✶
                  </Box>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 500, color: '#0a0a0b', letterSpacing: '-0.005em' }}>
                    Service · <Box component="span" sx={{ fontWeight: 700 }}>Google Business Profile</Box>
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '40px', sm: '58px', md: '76px' },
                  fontWeight: 700,
                  lineHeight: 1.03,
                  letterSpacing: '-0.04em',
                  color: '#0a0a0b'
                }}
              >
                Google Business Profile Optimization{' '}
                <Box component="span" sx={{ color: '#2563eb', fontStyle: 'italic' }}>
                  in Houston &amp; Katy, TX
                </Box>
              </Typography>

              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 19 }, fontWeight: 400, lineHeight: 1.55, letterSpacing: '-0.01em', color: '#52525b', maxWidth: 680 }}>
                We get your business into the <Box component="span" sx={{ color: '#0a0a0b', fontWeight: 600 }}>Google Map Pack</Box>: the top three
                local results on Google Search and Maps. That&apos;s where nearby customers find you, call you, and pick you first.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  endIcon={<SvgIcon sx={{ fontSize: 16 }}><ArrowRightIcon /></SvgIcon>}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    background: '#0a0a0b',
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: '#27272a', boxShadow: 'none' }
                  }}
                  variant="contained"
                >
                  Get a free profile audit
                </Button>
                <Button
                  component="a"
                  href="tel:+18138150142"
                  startIcon={<SvgIcon sx={{ fontSize: 16 }}><PhoneIcon /></SvgIcon>}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#0a0a0b',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    border: '1px solid #e4e4e7',
                    background: '#ffffff',
                    '&:hover': { borderColor: '#0a0a0b', background: '#fafafa' }
                  }}
                  variant="outlined"
                >
                  (813) 815-0142
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Why it matters (dark band) */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', background: '#0a0a0b', color: '#ffffff', px: { xs: 4, md: 7 }, py: { xs: 5, md: 7 } }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-10%',
                    width: 480,
                    height: 480,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37, 99, 235, 0.32) 0%, transparent 65%)',
                    filter: 'blur(60px)',
                    pointerEvents: 'none'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-40%',
                    left: '-10%',
                    width: 420,
                    height: 420,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.22) 0%, transparent 65%)',
                    filter: 'blur(70px)',
                    pointerEvents: 'none'
                  }}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1, mb: { xs: 4, md: 5 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.8)', letterSpacing: 1.4, textTransform: 'uppercase' }}>
                      Why it matters
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 13, md: 15 }, fontWeight: 700, letterSpacing: '-0.02em', color: 'rgba(255,255,255,0.85)' }}>
                    Mystic Media Film
                  </Typography>
                </Stack>

                <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 24, md: 38 }, fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.025em', color: '#ffffff', maxWidth: 900, position: 'relative', zIndex: 1 }}>
                  Your Google Business Profile (formerly Google My Business, or GMB) is often the very first thing a customer sees about you.{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    For most local businesses it brings in more calls and visits than the website does. It&apos;s the free listing that puts you on Google Maps and local search, with your hours, photos, reviews, and a one-tap way to call or get directions.
                  </Box>
                </Typography>

                <Box
                  sx={{
                    mt: { xs: 5, md: 7 },
                    pt: { xs: 3, md: 4 },
                    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${outcomes.length}, 1fr)` },
                    gap: { xs: 2, md: 0 },
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {outcomes.map((label, i) => (
                    <Stack key={label} direction="row" alignItems="baseline" spacing={1} sx={{ pl: { md: i === 0 ? 0 : 2 }, borderLeft: { md: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)' } }}>
                      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 10, fontWeight: 400, color: '#60a5fa', letterSpacing: 0.5 }}>
                        0{i + 1}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 500, color: '#ffffff', letterSpacing: '-0.01em' }}>
                        {label}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* What we optimize, image showcase */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>What we optimize</SectionLabel>
                  <Typography sx={sectionTitleSx}>
                    Every part of your profile,{' '}
                    <Box component="span" sx={{ color: '#2563eb' }}>tuned to rank.</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.55, color: '#52525b', maxWidth: 640 }}>
                    Google rewards complete, active profiles. We fill in and fine-tune every field that moves the needle on Google Search and Maps.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 2.5 } }}>
                  {optimizeItems.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: '#ffffff',
                        border: '1px solid #e4e4e7',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: '#2563eb',
                          boxShadow: '0 24px 48px -20px rgba(15, 23, 42, 0.2)'
                        }
                      }}
                    >
                      <Box sx={{ aspectRatio: '4 / 3', background: '#fafafa', borderBottom: '1px solid #f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2.5 }}>
                        <Box
                          component="img"
                          src={item.img}
                          alt={item.alt}
                          loading="lazy"
                          sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                        />
                      </Box>
                      <Stack spacing={1} sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 18, md: 20 }, fontWeight: 600, letterSpacing: '-0.02em', color: '#0a0a0b' }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                          {item.body}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Insights + conversion (two on-brand cards, side by side) */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>Insights &amp; conversion</SectionLabel>
                  <Typography sx={sectionTitleSx}>
                    Get found, then get{' '}
                    <Box component="span" sx={gradientTextSx}>chosen.</Box>
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 2.5 }, alignItems: 'stretch' }}>
                  {/* Insights card */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      border: '1px solid #e4e4e7',
                      background: '#ffffff',
                      p: { xs: 3.5, md: 4.5 },
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: '#2563eb', boxShadow: '0 16px 40px -22px rgba(37, 99, 235, 0.35)' }
                    }}
                  >
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.2, textTransform: 'uppercase', color: '#2563eb', mb: 2 }}>
                      Know what&apos;s working
                    </Typography>

                    <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 48, md: 60 }, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.04em', ...gradientTextSx }}>
                        8,210
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 500, color: '#52525b' }}>
                        profile interactions / month
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b', mb: 4, maxWidth: 380 }}>
                      Every call, click, and direction request from your profile, tracked and reported every month. You see the customers your profile brings in, not a guess.
                    </Typography>

                    <Stack spacing={2.25} sx={{ mt: 'auto' }}>
                      {insightStats.map((s) => (
                        <Box key={s.label}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13.5, fontWeight: 500, color: '#0a0a0b' }}>
                              {s.label}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 12, fontWeight: 500, color: '#52525b' }}>
                              {s.value}
                            </Typography>
                          </Stack>
                          <Box sx={{ height: 8, borderRadius: 999, background: '#f4f4f5', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${s.pct}%`, borderRadius: 999, background: BRAND_GRADIENT }} />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {/* Conversion card */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      border: '1px solid #e4e4e7',
                      background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                      p: { xs: 3.5, md: 4.5 },
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: '#2563eb', boxShadow: '0 16px 40px -22px rgba(37, 99, 235, 0.35)' }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '-30%',
                        right: '-15%',
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(37, 99, 235, 0.10) 0%, transparent 65%)',
                        filter: 'blur(40px)',
                        pointerEvents: 'none'
                      }}
                    />
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.2, textTransform: 'uppercase', color: '#2563eb', mb: 2, position: 'relative', zIndex: 1 }}>
                      Built to convert
                    </Typography>
                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 22, md: 26 }, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 1.5, position: 'relative', zIndex: 1 }}>
                      Turn searchers into customers.
                    </Typography>
                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14.5, fontWeight: 400, lineHeight: 1.6, color: '#52525b', mb: 4, maxWidth: 380, position: 'relative', zIndex: 1 }}>
                      Ranking is only half the job. We set up every way a customer can act, right from your profile, so a search turns into a booking.
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 'auto', position: 'relative', zIndex: 1 }}>
                      {leadActions.map((action) => (
                        <Box
                          key={action}
                          sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 999,
                            border: '1px solid #e4e4e7',
                            background: '#ffffff',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 13.5,
                            fontWeight: 500,
                            color: '#0a0a0b'
                          }}
                        >
                          {action}
                        </Box>
                      ))}
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 999,
                          background: BRAND_GRADIENT,
                          color: '#ffffff',
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 13.5,
                          fontWeight: 600,
                          boxShadow: '0 8px 20px -8px rgba(37, 99, 235, 0.5)'
                        }}
                      >
                        Request a quote
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Deliverables */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>What you get</SectionLabel>
                  <Typography sx={sectionTitleSx}>
                    A complete{' '}
                    <Box component="span" sx={{ color: '#2563eb' }}>Map Pack game plan.</Box>
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, md: 2 } }}>
                  {deliverables.map((d) => (
                    <Box
                      key={d.title}
                      sx={{
                        position: 'relative',
                        p: { xs: 3, md: 3.5 },
                        borderRadius: 3,
                        background: '#fafafa',
                        border: '1px solid #e4e4e7',
                        color: '#0a0a0b',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          borderColor: '#2563eb',
                          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)'
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                        <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 0.6, color: '#2563eb' }}>
                          {d.num} / {String(deliverables.length).padStart(2, '0')}
                        </Typography>
                      </Stack>

                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 24, md: 28 }, fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.03em', color: '#0a0a0b', mb: 1.5 }}>
                        {d.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#52525b' }}>
                        {d.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Process */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>How it works</SectionLabel>
                  <Typography sx={sectionTitleSx}>
                    Audit, optimize,{' '}
                    <Box component="span" sx={{ color: '#2563eb' }}>rank, repeat.</Box>
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${process.length}, 1fr)` }, gap: { xs: 3, md: 0 } }}>
                  {process.map((step, i) => (
                    <Stack key={step.title} spacing={2} sx={{ px: { md: 3 }, py: { xs: 0, md: 1 }, borderLeft: { md: i === 0 ? 'none' : '1px solid #e4e4e7' } }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', background: BRAND_GRADIENT, color: '#ffffff', fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px -6px rgba(37, 99, 235, 0.55)' }}>
                          {step.num}
                        </Box>
                        <Box sx={{ flex: 1, height: 2, borderRadius: 999, background: 'linear-gradient(90deg, rgba(37,99,235,0.35) 0%, rgba(6,182,212,0.05) 100%)' }} />
                      </Stack>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0a0a0b' }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                        {step.body}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Why Mystic */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={{ xs: 3, md: 4 }}>
                  <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
                    <SectionLabel>Why Mystic</SectionLabel>
                    <Typography sx={sectionTitleSx}>
                      A Houston team{' '}
                      <Box component="span" sx={gradientTextSx}>on your map.</Box>
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 16 }, fontWeight: 400, lineHeight: 1.6, color: '#52525b', maxWidth: 360 }}>
                    A small senior team, real local knowledge, and a process that respects your time and your budget.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${whyUs.length}, 1fr)` }, gap: { xs: 1.5, md: 2 } }}>
                  {whyUs.map((item, i) => (
                    <Box
                      key={item.title}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        background: '#ffffff',
                        border: '1px solid #e4e4e7',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#2563eb',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 16px 40px -22px rgba(37, 99, 235, 0.4)',
                          '& .why-glow': { opacity: 1 }
                        }
                      }}
                    >
                      <Box
                        className="why-glow"
                        sx={{
                          position: 'absolute',
                          top: '-40%',
                          right: '-20%',
                          width: 240,
                          height: 240,
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, transparent 65%)',
                          filter: 'blur(40px)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          pointerEvents: 'none'
                        }}
                      />
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: BRAND_GRADIENT,
                          color: '#ffffff',
                          fontFamily: '"Roboto Mono", monospace',
                          fontSize: 13,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          boxShadow: '0 8px 20px -8px rgba(37, 99, 235, 0.55)',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        0{i + 1}
                      </Box>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 1.5, position: 'relative', zIndex: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b', position: 'relative', zIndex: 1 }}>
                        {item.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* FAQ (collapsible accordion, matched to the home page) */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="md">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }} alignItems="center">
                <Stack spacing={2.5} alignItems="center">
                  <SectionLabel>Common questions</SectionLabel>
                  <Typography
                    align="center"
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '36px', md: '60px' },
                      fontWeight: 700,
                      lineHeight: 1.04,
                      letterSpacing: '-0.035em',
                      color: '#0a0a0b'
                    }}
                  >
                    Answers, not{' '}
                    <Box component="span" sx={{ color: '#2563eb' }}>fine print.</Box>
                  </Typography>
                </Stack>

                <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 760 }}>
                  {faqs.map((faq, i) => (
                    <FaqItem
                      key={faq.q}
                      q={faq.q}
                      a={faq.a}
                      i={i}
                      isOpen={openFaq === i}
                      onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                    />
                  ))}
                </Stack>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Final CTA */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={{ ...sectionShellSx, py: { xs: 14, md: 20 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', background: '#0a0a0b', color: '#ffffff', px: { xs: 4, md: 8 }, py: { xs: 7, md: 10 } }}>
                <Box sx={{ position: 'absolute', top: '-30%', right: '-10%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)', filter: 'blur(70px)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: '-30%', left: '-10%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 65%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

                <Stack spacing={5} alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.8)', letterSpacing: 1.4, textTransform: 'uppercase' }}>
                      Get In Touch
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: '40px', md: '72px' }, fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.04em', color: '#ffffff', maxWidth: 900 }}>
                    Ready to own{' '}
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                      the Houston Map Pack?
                    </Box>
                  </Typography>

                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 18 }, fontWeight: 400, lineHeight: 1.5, color: 'rgba(255,255,255,0.6)', maxWidth: 540 }}>
                    Tell us about your business and we&apos;ll send a free audit of your Google Business Profile: what&apos;s holding it back and how to climb. We reply within one business day.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      component={RouterLink}
                      href={paths.contact}
                      endIcon={<SvgIcon sx={{ fontSize: 16 }}><ArrowRightIcon /></SvgIcon>}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        background: '#ffffff',
                        color: '#0a0a0b',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { background: '#f4f4f5', boxShadow: 'none' }
                      }}
                      variant="contained"
                    >
                      Get my free audit
                    </Button>
                    <Button
                      component="a"
                      href="tel:+18138150142"
                      startIcon={<SvgIcon sx={{ fontSize: 16 }}><PhoneIcon /></SvgIcon>}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: '#ffffff',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'transparent',
                        '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' }
                      }}
                      variant="outlined"
                    >
                      Call (813) 815-0142
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Reveal>
          </Container>
        </Box>
      </main>
    </>
  );
};

export default Page;
